<script setup lang="ts">
import { computed, ref } from 'vue'

import { Memo, Service } from '@/shared/icons'
import { Icon } from '@/shared/ui/Icon'
import { Menu, MenuItem, MenuItemGroup } from '@/shared/ui/Menu'
import { Scrollbar } from '@/shared/ui/Scrollbar'
import { Stack } from '@/shared/ui/Stack'
import { adokLogos } from '@/apps/adok/app/assets'
import { useAccount } from '@/apps/adok/entities/account'
import { useOpenTelegramBot } from '@/apps/adok/features/telegram-account/manage'
import { CreateUserNoteModal } from '@/apps/adok/features/user-note/create'

import {
  SIDEBAR_FOOTER_NAVIGATION,
  SIDEBAR_NAVIGATION,
  SIDEBAR_TOOLS,
  SIDEBAR_TOOLS_TITLE
} from '../model/navigation'
import { useSidebarStore } from '../model/sidebar'
import AdminToggles from './AdminToggles.vue'

const sidebar = useSidebarStore()
const { isStaff } = useAccount()
const { openBot } = useOpenTelegramBot()

const { miniLogo, wideLogo } = adokLogos

const isCollapse = computed(() => !sidebar.opened)

const visible = (items: typeof SIDEBAR_NAVIGATION) =>
  items.filter((item) => !item.staffOnly || isStaff.value)

const navigation = computed(() => visible(SIDEBAR_NAVIGATION))
const tools = computed(() => visible(SIDEBAR_TOOLS))
const footer = computed(() => visible(SIDEBAR_FOOTER_NAVIGATION))

const notesOpen = ref(false)
</script>

<template>
  <Stack :gap="0" class="sidebar">
    <router-link :to="{ name: 'dashboard' }" class="logo-container">
      <img v-if="isCollapse" :src="miniLogo" />
      <img v-else :src="wideLogo" />
    </router-link>
    <Scrollbar>
      <Menu :collapse="isCollapse" fill>
        <router-link
          v-for="item in navigation"
          :key="item.routeName"
          :to="{ name: item.routeName }"
        >
          <MenuItem>
            <Icon><component :is="item.icon" /></Icon>
            <template #title>
              <span>{{ item.label }}</span>
            </template>
          </MenuItem>
        </router-link>
        <MenuItemGroup :title="isCollapse ? '' : SIDEBAR_TOOLS_TITLE">
          <router-link
            v-for="item in tools"
            :key="item.routeName"
            :to="{ name: item.routeName }"
          >
            <MenuItem>
              <Icon><component :is="item.icon" /></Icon>
              <template #title>
                <span>{{ item.label }}</span>
              </template>
            </MenuItem>
          </router-link>
        </MenuItemGroup>
        <MenuItemGroup
          v-if="isStaff"
          :title="isCollapse ? '' : 'Admin'"
          class="sidebar-admin"
        >
          <AdminToggles />
          <MenuItem @click="notesOpen = true">
            <Icon><Memo /></Icon>
            <template #title>
              <span>Add note</span>
            </template>
          </MenuItem>
        </MenuItemGroup>
      </Menu>
    </Scrollbar>

    <Menu :collapse="isCollapse" fill class="sidebar-footer">
      <router-link
        v-for="item in footer"
        :key="item.routeName"
        :to="{ name: item.routeName }"
      >
        <MenuItem>
          <Icon><component :is="item.icon" /></Icon>
          <template #title>
            <span>{{ item.label }}</span>
          </template>
        </MenuItem>
      </router-link>
      <a @click="openBot()">
        <MenuItem>
          <Icon><Service /></Icon>
          <template #title>
            <span>Support (Telegram)</span>
          </template>
        </MenuItem>
      </a>
    </Menu>

    <CreateUserNoteModal
      v-if="isStaff"
      :model-value="notesOpen"
      @close="notesOpen = false"
    />
  </Stack>
</template>

<style lang="scss" scoped>
.sidebar > :deep(.el-scrollbar) {
  flex: 1;
  min-height: 0;
}

.sidebar-footer {
  flex: none;
  height: auto;
  border-top: 1px solid var(--el-border-color-lighter);
}

.sidebar-admin {
  --el-menu-text-color: var(--el-color-warning);
  --el-menu-hover-text-color: var(--el-color-warning);
  margin-bottom: 20px;
}

.logo-container {
  display: block;
  background: var(--el-bg-color);
  text-align: center;
  height: 50px;
  cursor: pointer;
  img {
    max-width: 100%;
    padding: 5px;
    height: 100%;
  }
}
</style>
